import { ActionButton, FormInputRow, Input } from '@emerald-works-nova/components'
import { useEvent } from '@emerald-works/react-event-bus-client'
import { Autocomplete, Box, Collapse, Divider, FormControl, FormHelperText, Grid, InputLabel, List, ListItem, ListItemText, MenuItem, Select, Switch, TextField, Typography, useTheme } from '@mui/material'
import { KeyboardArrowRight } from '@mui/icons-material'
import React, { useState, useMemo } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useFeatureFormContext } from './feature-form-context'
import { useStyles } from './style'
import { useDispatch } from 'react-redux'
import { BannerStatic } from '../../../components'
import { TenantEditSlice } from '../../../reducers'
import { TimePicker } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import timezones from 'timezones-list'
import { format, parse } from 'date-fns'

const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday']

const MenuSwitchInput = () => {
  const classes = useStyles()
  const { spacing } = useTheme()
  const dispatch = useDispatch()
  const { tenant } = useFeatureFormContext()
  const form = useForm({
    mode: 'onChange',
    defaultValues: {
      sendDay: tenant.features?.weeklyEmail?.sendDay || 'Monday',
      senderName: tenant.features?.weeklyEmail?.senderName || 'Mind Tools',
      emailSubject: tenant.features?.weeklyEmail?.emailSubject || 'Weekly Recommendations',
      timeZone: tenant.features?.weeklyEmail?.timeZone || 'Europe/London',
      bespokeSenderAddress: tenant.features?.weeklyEmail?.bespokeSenderAddress || '',
      sendTime: tenant.features?.weeklyEmail?.sendTime
        ? parse(tenant.features?.weeklyEmail?.sendTime, 'p', new Date())
        : parse('08:00 AM', 'p', new Date())
    }
  })
  const { getValues, setValue, formState: { errors, isValid } } = form

  const [isLoading, setIsLoading] = useState(false)
  const [checked, setChecked] = useState(!!tenant.features?.weeklyEmail?.enabled)
  const [isCollapsed, setIsCollapsed] = useState(false)
  const timezoneOptions = useMemo(() => timezones.map(tz => ({ label: `(GMT${tz.utc}) ${tz.tzCode}`, id: tz.tzCode })), [])

  const handleSwitch = () => setChecked(checked => {
    if (!checked && !tenant.features?.weeklyEmail) {
      // Set default values
      setValue('sendDay', 'Monday')
      setValue('senderName', 'Mind Tools')
      setValue('emailSubject', 'Weekly Recommendations')
      setValue('timeZone', 'Europe/London')
      setValue('sendTime', parse('08:00 AM', 'p', new Date()))
      setValue('bespokeSenderAddress', 'whatsnew@discover.mindtools.com')
    }

    if (checked) setIsCollapsed(false)
    return !checked
  })
  const handleClickListItem = () => setIsCollapsed(isCollapsed => !isCollapsed)

  const [tenantUpdate] = useEvent([
    {
      eventName: 'tenantUpdate',
      onSuccess: (tenant) => {
        BannerStatic.show({ label: 'Weekly email settings updated' })
        setIsLoading(false)
        dispatch(TenantEditSlice.actions.updateTenantFeatures(tenant.features))
      },
      onError: () => {
        BannerStatic.show({
          label: 'There was an error saving the toolkit features configuration',
          isError: true
        })
        setIsLoading(false)
      }
    }
  ])

  const handleSave = () => {
    tenantUpdate.trigger({
      pk: tenant.pk,
      features: {
        ...tenant.features,
        weeklyEmail: {
          ...getValues(),
          sendTime: format(getValues().sendTime, 'p'),
          enabled: checked
        }
      }
    })
    setIsLoading(true)
  }

  const isSaveDisabled = useMemo(() => isLoading || (!isValid && checked), [isLoading, isValid, checked])

  return (
    <Grid container className={classes.featContainer}>
      <Grid item xs={12}>
        <Box
          border={1}
          borderColor='#E0E0E0'
          style={{ padding: spacing(1, 4) }}
        >
          <Box
            display='flex'
            style={{ justifyContent: 'space-between', alignItems: 'flex-start' }}
          >
            <Typography variant='body2' style={{ marginTop: spacing(3.5) }}>
              Weekly Emails
            </Typography>
            <Box display='flex' style={{ justifyContent: 'flex-end', alignItems: 'flex-start', padding: spacing(1) }}>
              <Box visibility={checked ? 'visible' : 'hidden'}>
                <List component='nav' aria-label='Device settings'>
                  <ListItem
                    button
                    aria-haspopup='true'
                    aria-controls='lock-menu'
                    aria-label='when device is locked'
                    onClick={handleClickListItem}
                  >
                    <ListItemText disableTypography style={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant='body2'>Configure Email Settings</Typography>
                      <KeyboardArrowRight />
                    </ListItemText>
                  </ListItem>
                </List>
              </Box>
              {checked && (
                <Divider
                  orientation='vertical'
                  flexItem
                  style={{
                    margin: spacing(3, 0),
                    borderColor: 'rgba(0, 0, 0, 0.38)'
                  }}
                />
              )}
              <Box style={{ padding: spacing(2, 0.5) }}>
                <Switch
                  color='primary'
                  checked={checked}
                  onChange={handleSwitch}
                  disabled={isLoading}
                />
              </Box>
            </Box>

          </Box>
          <Collapse in={isCollapsed}>
            <Box className={classes.formCtn}>
              <Grid item xs={12}>
                <FormInputRow
                  title='Send Day'
                  description='Set the day for the email to be delivered.'
                  smallFormTitles
                  InputComponent={
                    <Controller
                      name='sendDay'
                      control={form.control}
                      rules={{ required: true }}
                      render={({ field }) =>
                        <FormControl sx={{ width: '100%' }} error={errors.sendDay !== undefined} variant='outlined' className={classes.selectControl}>
                          <InputLabel id='send-day-select-lbl'>Send Day</InputLabel>
                          <Select
                            variant='outlined'
                            {...field}
                            id='send-day-select'
                            labelId='send-day-select-lbl'
                            value={field.value || ''}
                          >
                            {WEEKDAYS.map(opt => <MenuItem value={opt} key={opt}>{opt}</MenuItem>)}
                          </Select>
                          {errors.sendDay !== undefined && (
                            <FormHelperText>Send Day is required</FormHelperText>
                          )}
                        </FormControl>}
                    />
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <FormInputRow
                  title='Time Zone'
                  description='Set the client’s time zone.'
                  smallFormTitles
                  InputComponent={
                    <Controller
                      name='timeZone'
                      control={form.control}
                      rules={{ required: true }}
                      render={({ field }) =>
                        <FormControl sx={{ width: '100%' }} error={errors.timeZone !== undefined} variant='outlined' className={classes.selectControl}>
                          <Autocomplete
                            disablePortal
                            options={timezoneOptions}
                            isOptionEqualToValue={(option, value) => option.id === value}
                            {...field}
                            onChange={(_, value) => {
                              field.onChange(value?.id)
                            }}
                            renderInput={(params) => <TextField {...params} label='Time Zone' />}
                          />
                          {errors.timeZone !== undefined && (
                            <FormHelperText>Time Zone is required</FormHelperText>
                          )}
                        </FormControl>}
                    />
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <FormInputRow
                  title='Send Time'
                  description='Set the time for the email to be delivered in the defined time zone.'
                  smallFormTitles
                  InputComponent={
                    <Controller
                      name='sendTime'
                      control={form.control}
                      rules={{ required: true }}
                      render={({ field }) =>
                        <FormControl sx={{ width: '100%' }} error={errors.sendTime !== undefined} variant='outlined' className={classes.selectControl}>
                          <LocalizationProvider dateAdapter={AdapterDateFns} dateFormats={['']}>
                            <TimePicker
                              {...field}
                              label='Send Time'
                            />
                          </LocalizationProvider>
                          {errors.sendTime !== undefined && (
                            <FormHelperText>Send Time is required</FormHelperText>
                          )}
                        </FormControl>}
                    />
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <FormInputRow
                  title='Sender Name'
                  description='Specify a name for the sender of the Weekly Email.'
                  smallFormTitles
                  InputComponent={
                    <Input
                      label='Sender Name'
                      name='senderName'
                      control={form.control}
                      valid={errors.senderName === undefined}
                      showErrors
                    />
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <FormInputRow
                  title='Email Subject'
                  description='Specify the subject for the Weekly Email.'
                  smallFormTitles
                  InputComponent={
                    <Input
                      label='Email Subject'
                      name='emailSubject'
                      control={form.control}
                    />
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <FormInputRow
                  title='Bespoke Sender Address'
                  description='Specify the Bespoke Sender Address for the Weekly Email. The Sender Address must exist'
                  smallFormTitles
                  InputComponent={
                    <Input
                      label='Bespoke Sender Address'
                      name='bespokeSenderAddress'
                      control={form.control}
                    />
                  }
                />
              </Grid>
            </Box>
          </Collapse>
          <Divider style={{ marginBottom: spacing(1) }} />
          <Box className={classes.ctnControls} display='flex' justifyContent='flex-end'>
            <ActionButton
              onClick={handleSave}
              disabled={isSaveDisabled}
              variant='primary'
            > Save
            </ActionButton>
          </Box>
        </Box>

      </Grid>
    </Grid>
  )
}

export default MenuSwitchInput
